import useTranslation from "next-translate/useTranslation"
import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { ICategory } from "@api/schema"
import ErrorPage from "@components/ErrorPage"
import SpinnerPage from "@components/SpinnerPage"
import { newLoadCollectionAction } from "@redux/helper/actions"
import { IRequestState } from "@redux/helper/state"
import { AppState } from "@redux/reducer"
import { selectCollectionUsecaseState } from "@redux/reducer/data"
import { EntityType, ScopeTypes } from "@redux/reduxTypes"

/**
 * structure of what the useCategories hook returns
 */
interface IUseCategoryRequestData {
  categories: ICategory[]
  categoryRequest: IRequestState
  errorOrSpinnerPage: JSX.Element
}

/**
 * Hook to use the categories.
 * Loads them if they are not present yet.
 *
 * NOTE: known issues: unable to avoid a request to the api, if another request is already running: FCP-1304
 *
 * @param loadIfNotPresent should the hook load the categories if they are not in state?
 * To avoid unnessecary loads, e.g. if no project slug is given. default: true
 * @returns categories, category request and error/spinner page to be handled/used/returned by the caller
 */
export const useCategories = (loadIfNotPresent = true): IUseCategoryRequestData => {
  const { t } = useTranslation("common")
  const dispatch = useDispatch()

  const categoryRequest = useSelector((state: AppState) =>
    selectCollectionUsecaseState(state, EntityType.Category, ScopeTypes.CategoriesLoading)
  )
  // @todo multimandant: filter categories for current process: https://futureprojects.atlassian.net/browse/FCP-1360
  const categories = categoryRequest?.getItems<ICategory>()

  useEffect(() => {
    if ((!categoryRequest ||
      (!categoryRequest.loaded && !categoryRequest.isLoading && !categoryRequest.loadingError))
      && loadIfNotPresent
    ) {
      dispatch(newLoadCollectionAction(EntityType.Category, {}, ScopeTypes.CategoriesLoading, true))
    }
  }, [JSON.stringify(categoryRequest), categories, loadIfNotPresent])


  let errorOrSpinnerPage: JSX.Element = null
  // if categories could not be loaded and an error returned, display the error
  if (categoryRequest?.loadingError) {
    errorOrSpinnerPage = <ErrorPage statusCode={404} error={categoryRequest?.loadingError} />
  } else if (!categoryRequest || categoryRequest?.isLoading) {
    // if there is no request yet or it is loading right now: show spinner
    errorOrSpinnerPage = <SpinnerPage title={t("page.loading.title")} description={t("page.loading.title")} />
  }

  return { categories, categoryRequest, errorOrSpinnerPage }
}